import React from 'react';
import { UserIcon, ArrowUpRightIcon, ArrowDownRightIcon } from 'lucide-react';
import type { LoanOfficer, MetricType, PeriodInfo } from '../../types';
import { ProgressBar } from '../ui/ProgressBar';
import { Badge } from '../ui/Badge';
import { getProgressPercentage, getProgressStatus, calculateProjection, getProjectionLabel } from '../../utils/calculations';
interface LoanOfficerRowProps {
  loanOfficer: LoanOfficer;
  period: PeriodInfo;
  metric: MetricType;
}
export function LoanOfficerRow({
  loanOfficer,
  period,
  metric
}: LoanOfficerRowProps) {
  const current = metric === 'softPull' ? loanOfficer.softPullCount : loanOfficer.submittedCount;
  const goal = metric === 'softPull' ? loanOfficer.softPullGoal : loanOfficer.submittedGoal;
  const percentage = getProgressPercentage(current, goal);
  const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
  const projectionLabel = getProjectionLabel(projected, goal);
  const status = getProgressStatus(getProgressPercentage(projected, goal));
  const isAboveGoal = projected >= goal;
  return <div className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0" data-id="element-36">
      {/* Name */}
      <div className="flex items-center gap-2.5 w-44 flex-shrink-0" data-id="element-37">
        <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-38">
          <UserIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-39" />
        </div>
        <span className="text-sm font-medium text-gray-900 truncate" data-id="element-40">
          {loanOfficer.name}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="flex-1 min-w-0" data-id="element-41">
        <ProgressBar current={current} goal={goal} size="sm" showLabel={false} data-id="element-42" />
      </div>

      {/* Count */}
      <div className="w-20 text-right flex-shrink-0" data-id="element-43">
        <span className="text-sm font-semibold text-gray-900" data-id="element-44">{current}</span>
        <span className="text-sm text-gray-400" data-id="element-45"> / {goal}</span>
      </div>

      {/* Percentage */}
      <div className="w-16 text-right flex-shrink-0" data-id="element-46">
        <span className="text-sm font-medium text-gray-700" data-id="element-47">{percentage}%</span>
      </div>

      {/* Projection */}
      <div className="w-48 flex-shrink-0 hidden lg:flex items-center gap-1.5" data-id="element-48">
        {isAboveGoal ? <ArrowUpRightIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-49" /> : <ArrowDownRightIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" data-id="element-50" />}
        <span className="text-xs text-gray-500 truncate" data-id="element-51">
          {projectionLabel}
        </span>
      </div>

      {/* Status Badge */}
      <div className="w-24 flex-shrink-0 flex justify-end" data-id="element-52">
        <Badge status={status} size="sm" data-id="element-53" />
      </div>
    </div>;
}